/**
 * Predictive search component.
 * Fetches results from Shopify's predictive search API as the user types.
 * Results are <a role="option"> children of a [role="listbox"] container,
 * with arrow / Enter keyboard navigation.
 */
const EMPTY_HTML = '<div class="pp-psearch-empty" role="presentation">Start typing to search…</div>';
const SEARCHING_HTML = '<div class="pp-psearch-empty" role="presentation">Searching…</div>';

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.openBtn = this.querySelector('[data-open-search]');
    this.closeBtn = this.querySelector('[data-close-search]');
    this.dropdown = this.querySelector('.predictive-search-results');
    this.input = this.querySelector('[data-search-input]');
    this.resultsContainer = this.querySelector('[data-search-results]');
    this.debounceTimer = null;
    this.activeIndex = -1;

    this.openBtn?.addEventListener('click', () => this.open());
    this.closeBtn?.addEventListener('click', () => this.close());

    this.input?.addEventListener('input', () => {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.search(), 300);
    });

    this.input?.addEventListener('keydown', (e) => this.#handleKey(e));

    this.#bindOutsideClick();
  }

  #bindOutsideClick() {
    this.outsideHandler = (e) => {
      if (this.isOpen && !this.contains(e.target)) this.close();
    };
  }

  get options() {
    return this.resultsContainer?.querySelectorAll('[role="option"]') ?? [];
  }

  get isOpen() {
    return this.dropdown?.classList.contains('is-active');
  }

  open() {
    if (this.isOpen) return;
    this.dropdown?.classList.add('is-active');
    this.openBtn?.setAttribute('aria-expanded', 'true');
    document.addEventListener('click', this.outsideHandler);
    setTimeout(() => this.input?.focus(), 50);
  }

  close() {
    if (!this.isOpen) return;
    this.dropdown?.classList.remove('is-active');
    this.openBtn?.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', this.outsideHandler);
    if (this.input) this.input.value = '';
    if (this.resultsContainer) this.resultsContainer.innerHTML = EMPTY_HTML;
    this.activeIndex = -1;
  }

  #handleKey(e) {
    if (e.key === 'Escape') {
      this.close();
      this.openBtn?.focus();
      return;
    }
    const opts = this.options;
    if (!opts.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.#setActive((this.activeIndex + 1) % opts.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.#setActive((this.activeIndex - 1 + opts.length) % opts.length);
    } else if (e.key === 'Enter' && this.activeIndex >= 0) {
      e.preventDefault();
      opts[this.activeIndex]?.click();
    }
  }

  #setActive(idx) {
    const opts = this.options;
    opts.forEach((o, i) => {
      o.classList.toggle('is-active', i === idx);
      o.setAttribute('aria-selected', i === idx ? 'true' : 'false');
    });
    this.activeIndex = idx;
    const el = opts[idx];
    if (el) {
      el.scrollIntoView({ block: 'nearest' });
      this.input?.setAttribute('aria-activedescendant', el.id || '');
    }
  }

  async search() {
    const query = this.input?.value?.trim();
    if (!query || query.length < 2) {
      this.resultsContainer.innerHTML = EMPTY_HTML;
      this.activeIndex = -1;
      return;
    }

    this.resultsContainer.innerHTML = SEARCHING_HTML;

    try {
      const response = await fetch(
        `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,page,article&resources[limit]=6`
      );
      const data = await response.json();
      const products = data.resources?.results?.products || [];
      const pages = data.resources?.results?.pages || [];
      const articles = data.resources?.results?.articles || [];

      if (!products.length && !pages.length && !articles.length) {
        this.resultsContainer.innerHTML = `<div class="pp-psearch-empty" role="presentation">No results for &ldquo;${this.#escape(query)}&rdquo;</div>`;
        this.activeIndex = -1;
        return;
      }

      let optionIndex = 0;
      const optId = (i) => `${this.input.id}-opt-${i}`;
      let html = '';

      if (products.length) {
        html += '<div class="pp-psearch-group" role="presentation">';
        html += '<p class="pp-psearch-group-h" role="presentation">Products</p>';
        for (const p of products) {
          const img = p.image
            ? `<img src="${p.image}" alt="" loading="lazy" width="40" height="40" class="pp-psearch-img">`
            : '<div class="pp-psearch-img pp-psearch-img-fallback" aria-hidden="true"></div>';
          html += `<a id="${optId(optionIndex++)}" role="option" aria-selected="false" href="${p.url}" class="pp-psearch-item no-underline">
            ${img}
            <span class="pp-psearch-item-body">
              <span class="pp-psearch-item-title">${this.#escape(p.title)}</span>
              <span class="pp-psearch-item-meta">${p.price ?? ''}</span>
            </span>
          </a>`;
        }
        html += '</div>';
      }
      if (pages.length) {
        html += '<div class="pp-psearch-group" role="presentation"><p class="pp-psearch-group-h" role="presentation">Pages</p>';
        for (const p of pages) {
          html += `<a id="${optId(optionIndex++)}" role="option" aria-selected="false" href="${p.url}" class="pp-psearch-item pp-psearch-item-text no-underline">${this.#escape(p.title)}</a>`;
        }
        html += '</div>';
      }
      if (articles.length) {
        html += '<div class="pp-psearch-group" role="presentation"><p class="pp-psearch-group-h" role="presentation">Articles</p>';
        for (const a of articles) {
          html += `<a id="${optId(optionIndex++)}" role="option" aria-selected="false" href="${a.url}" class="pp-psearch-item pp-psearch-item-text no-underline">${this.#escape(a.title)}</a>`;
        }
        html += '</div>';
      }
      html += `<a id="${optId(optionIndex++)}" role="option" aria-selected="false" href="/search?q=${encodeURIComponent(query)}" class="pp-psearch-all no-underline">View all results</a>`;

      this.resultsContainer.innerHTML = html;
      this.activeIndex = -1;
    } catch (error) {
      console.error('Predictive search error:', error);
      this.resultsContainer.innerHTML = '<div class="pp-psearch-empty" role="presentation">Something went wrong. Try again.</div>';
      this.activeIndex = -1;
    }
  }

  #escape(str) {
    const el = document.createElement('span');
    el.textContent = str;
    return el.innerHTML;
  }
}

customElements.define('predictive-search', PredictiveSearch);

export { PredictiveSearch };
